// The public waitlist page (M3-22), at /waitlist for anyone while the Cell serves it: someone whose industry LAMP
// does not serve yet leaves their details, what they want LAMP for, and the four price questions (docs/research/
// pricing.md §6), with their consent to the privacy notice. LAMP keeps the entry as its own data.
import { useState, type FormEvent } from "react";
import { ApiError } from "../http.js";
import { otherLanguage, useLanguage, useWords } from "../i18n/words.js";
import { noticeHref } from "../legal/Notice.js";
import type { SignupApi, WaitlistInput } from "./api.js";

export const WAITLIST_PATH = "/waitlist";

const PRICES = ["tooCheap", "cheap", "expensive", "tooExpensive"] as const;
type Price = (typeof PRICES)[number];

/** Rupees as typed, with or without commas; undefined unless a whole number. */
const rupees = (value: string): number | undefined => {
  const digits = value.replace(/[,\s₹]/g, "");
  return /^\d{1,8}$/.test(digits) ? Number(digits) : undefined;
};

export function Waitlist({ api }: { api: Pick<SignupApi, "joinWaitlist"> }) {
  const w = useWords();
  const { language, setLanguage } = useLanguage();
  const [answers, setAnswers] = useState({
    name: "",
    businessName: "",
    contact: "",
    city: "",
    industry: "",
    wants: "",
  });
  const [prices, setPrices] = useState<Record<Price, string>>({
    tooCheap: "",
    cheap: "",
    expensive: "",
    tooExpensive: "",
  });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const set = (key: keyof typeof answers) => (value: string) => setAnswers((a) => ({ ...a, [key]: value }));
  const label: Record<Price, string> = {
    tooCheap: w.waitlistTooCheap,
    cheap: w.waitlistCheap,
    expensive: w.waitlistExpensive,
    tooExpensive: w.waitlistTooExpensive,
  };

  const join = async (event: FormEvent) => {
    event.preventDefault();
    if (!consent) return;
    const [tooCheap, cheap, expensive, tooExpensive] = PRICES.map((p) => rupees(prices[p]));
    if (tooCheap === undefined || cheap === undefined || expensive === undefined || tooExpensive === undefined)
      return setProblem(w.waitlistCheck);
    if (!(tooCheap <= cheap && cheap <= expensive && expensive <= tooExpensive))
      return setProblem(w.waitlistPricesOrder);
    const input: WaitlistInput = {
      name: answers.name.trim(),
      businessName: answers.businessName.trim(),
      contact: answers.contact.trim(),
      city: answers.city.trim(),
      industry: answers.industry,
      wants: answers.wants.trim(),
      tooCheap,
      cheap,
      expensive,
      tooExpensive,
      consent,
    };
    setBusy(true);
    setProblem(undefined);
    try {
      await api.joinWaitlist(input);
      setJoined(true);
    } catch (error) {
      setProblem(error instanceof ApiError && error.status === 429 ? w.waitlistTooMany : w.waitlistCheck);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="sign-in public-page" id="main">
      <p className="sign-in-language">
        <button type="button" className="chip" onClick={() => setLanguage(otherLanguage(language))}>
          {w.languageSwitch}
        </button>
      </p>
      <h1>{w.waitlistTitle}</h1>
      {joined ? (
        <p role="status">{w.waitlistJoined}</p>
      ) : (
        <>
          <p>{w.waitlistIntro}</p>
          <form onSubmit={join}>
            <label className="field">
              {w.waitlistName}
              <input
                value={answers.name}
                required
                maxLength={120}
                autoComplete="name"
                onChange={(e) => set("name")(e.target.value)}
              />
            </label>
            <label className="field">
              {w.waitlistBusiness}
              <input
                value={answers.businessName}
                required
                maxLength={200}
                autoComplete="organization"
                onChange={(e) => set("businessName")(e.target.value)}
              />
            </label>
            <label className="field">
              {w.waitlistContact}
              <input
                value={answers.contact}
                required
                maxLength={254}
                autoComplete="email"
                onChange={(e) => set("contact")(e.target.value)}
              />
            </label>
            <label className="field">
              {w.waitlistCity}
              <input
                value={answers.city}
                required
                maxLength={120}
                autoComplete="address-level2"
                onChange={(e) => set("city")(e.target.value)}
              />
            </label>
            <label className="field">
              {w.waitlistIndustry}
              <select value={answers.industry} required onChange={(e) => set("industry")(e.target.value)}>
                <option value="">{w.waitlistIndustryPick}</option>
                {w.waitlistIndustries.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              {w.waitlistWants}
              <textarea
                value={answers.wants}
                required
                maxLength={1000}
                rows={3}
                onChange={(e) => set("wants")(e.target.value)}
              />
            </label>
            <fieldset className="prices">
              <legend>{w.waitlistPricesHeading}</legend>
              {PRICES.map((p) => (
                <label className="field" key={p}>
                  {label[p]}
                  <input
                    value={prices[p]}
                    required
                    inputMode="numeric"
                    onChange={(e) => setPrices((all) => ({ ...all, [p]: e.target.value }))}
                  />
                </label>
              ))}
            </fieldset>
            <label className="check">
              <input type="checkbox" checked={consent} required onChange={(e) => setConsent(e.target.checked)} />
              {w.waitlistConsent}
            </label>
            <p>
              <a href={noticeHref("privacy-notice", language)}>{w.legalPrivacyNotice}</a>
              {" · "}
              <a href="/trust">{w.signupPrivacyLink}</a>
            </p>
            <button type="submit" className="btn btn-go" disabled={busy || !consent}>
              {w.waitlistSubmit}
            </button>
          </form>
        </>
      )}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
    </main>
  );
}
